"use client";

import React, {useEffect, useState} from "react";
import {motion, useMotionValue, useSpring} from "framer-motion";
import {cn} from "@/lib/utils";

type YRange = { min: number; max: number; step: number; decimals?: number };

export default function CrosshairOverlay({
  parentRef,
  yRange,
  onYChange,
}: {
  parentRef: React.RefObject<HTMLElement | null>;
  yRange: YRange;
  onYChange?: (raw: number, snapped: number, index: number) => void;
}) {
  const {min, max, step, decimals = 3} = yRange;

  const x = useMotionValue(-9999);
  const y = useMotionValue(-9999);
  const xS = useSpring(x, {stiffness: 600, damping: 50, mass: 0.4});
  const yS = useSpring(y, {stiffness: 600, damping: 50, mass: 0.4});

  const [inside, setInside] = useState(false);
  const [label, setLabel] = useState<string>("");

  useEffect(() => {
    const el = parentRef.current;
    if (!el) return;
    if (window.matchMedia("(pointer: coarse)").matches) return;

    const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));

    const onMove = (e: MouseEvent) => {
      const r = el.getBoundingClientRect();
      const cx = e.clientX - r.left;
      const cy = e.clientY - r.top;
      const clampedX = clamp(cx, 0, r.width);
      const clampedY = clamp(cy, 0, r.height);

      x.set(clampedX);
      y.set(clampedY);

      const t = 1 - clampedY / r.height;
      const raw = min + (max - min) * t;
      const snapped = clamp(Math.round((raw - min) / step) * step + min, min, max);
      const idx = Math.round((snapped - min) / step);

      setLabel(snapped.toFixed(decimals));
      onYChange?.(raw, snapped, idx);
    };

    const onEnter = () => setInside(true);

    const onLeave = () => {
      setInside(false);
    };

    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseenter", onEnter);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseenter", onEnter);
      el.removeEventListener("mouseleave", onLeave);
    };
  }, [parentRef, min, max, step, decimals, onYChange, x, y]);

  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 z-[60]"
    >
      {/* lines */}
      <motion.div
        style={{x: xS}}
        className="absolute inset-y-0 w-px bg-[#D9F9B9]/20"
      />
      <motion.div
        style={{y: yS}}
        className="absolute inset-x-0 h-px bg-[#D9F9B9]/20"
      />

      {label && (
        <motion.div
          style={{y: yS}}
          className="absolute w-full"
        >
          <div className="absolute right-0 bg-[#D9F9B9] px-[1rem] min-h-[4rem] -translate-y-1/2 py-[.5rem] flex flex-col items-center justify-center">
            <p className="mb-0 text-[2rem] tabular-nums leading-none font-medium text-[#0A1A1D]">
              {label}
            </p>
            <div className={cn(
              "absolute h-full w-[2rem] top-0 right-full z-10 bg-[#D9F9B9] border-r-[2px] border-r-[#D9F9B9]",
              "[clip-path:polygon(0_2rem,2rem_0,100%_0,100%_100%,2rem_100%,0_calc(100%-2rem))]"
            )}/>
          </div>
        </motion.div>
      )}
    </div>
  );
}